import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { BrowserCheckCta } from '@/components/browser-check-cta'

export const Route = createFileRoute('/_site/guides/browser-checks')({
  component: BrowserChecksGuidePage,
  head: () => ({
    meta: [
      {
        title: 'Browser Checks: Screenshots & Core Web Vitals — Uptrack',
      },
      {
        name: 'description',
        content:
          'A 200 OK does not mean the page rendered. Browser checks load your site in a real browser, take a screenshot and track Core Web Vitals.',
      },
      { property: 'og:title', content: 'Browser Checks & Core Web Vitals Monitoring — Uptrack' },
      {
        property: 'og:description',
        content:
          'Monitor what your users actually see: real-browser screenshots and Core Web Vitals, on a schedule, with alerts.',
      },
      {
        property: 'og:image',
        content: 'https://uptrack.app/og/guides-browser-checks.png',
      },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/guides/browser-checks' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'Browser Checks: Monitor Screenshots & Core Web Vitals',
          description:
            'How browser checks load your site in a real headless browser to capture a screenshot, verify rendering, and track Core Web Vitals.',
          datePublished: '2026-09-30',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: { '@type': 'Organization', name: 'Uptrack' },
        }),
      },
    ],
  }),
})

function BrowserChecksGuidePage() {
  return (
    <>
      <>
        <article
          className="article-prose container mx-auto max-w-2xl px-4 py-16"
        >
          <p className="font-sans mb-3 text-sm font-medium uppercase tracking-wide text-primary">Monitoring</p>
          <h1 className="font-sans mb-4 text-4xl font-semibold">Browser checks: monitor what users actually see</h1>
          <p className="mb-8 text-lg leading-[1.7] text-muted-foreground">
            An uptime check asks one question: did the server answer? A browser check asks a harder one: did the
            page actually <em>render</em>, and how fast? This guide explains how browser checks work and when to
            reach for one instead of a plain HTTP check.
          </p>

          <section className="mb-10">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Why 200 OK isn't enough</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              A modern page is a program. The HTML arrives in milliseconds, then JavaScript runs, fonts load, an
              API call fills the cart, and a framework paints the screen. An HTTP monitor sees only the first
              step — it gets a 200 and moves on. But your users wait for the last step.
            </p>
            <p className="text-lg leading-[1.7] text-body">
              That gap is where silent failures live: a blank white screen from a JavaScript error, a checkout
              button that never mounts, a third-party widget that hangs the page. The server is &ldquo;up.&rdquo;
              The experience is broken. A browser check catches it because it loads the page the same way a
              visitor would.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-sans mb-4 text-2xl font-semibold">What a browser check captures</h2>
            <ul className="space-y-3">
              <li className="text-lg leading-[1.7] text-body">
                <strong className="text-foreground">A real screenshot.</strong> Proof of what rendered, on every
                run — so you can see a broken layout, not just read about a status code.
              </li>
              <li className="text-lg leading-[1.7] text-body">
                <strong className="text-foreground">A pass/fail verdict.</strong> The page either loaded and your
                script succeeded, or it didn't — captured as an up/down result with the failing step.
              </li>
              <li className="text-lg leading-[1.7] text-body">
                <strong className="text-foreground">Core Web Vitals.</strong> LCP, CLS, FCP, and TTFB, measured in
                a real browser — performance regressions you'd otherwise only hear about from users.
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Core Web Vitals, briefly</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              These are the metrics Google uses to score real-world experience. You don't need to memorize them,
              but knowing the thresholds helps you read a result:
            </p>
            <ul className="space-y-3">
              <li className="text-lg leading-[1.7] text-body">
                <strong className="text-foreground">LCP</strong> (Largest Contentful Paint) — when the main
                content appears. Good ≤ 2.5s.
              </li>
              <li className="text-lg leading-[1.7] text-body">
                <strong className="text-foreground">CLS</strong> (Cumulative Layout Shift) — how much the page
                jumps around as it loads. Good ≤ 0.1.
              </li>
              <li className="text-lg leading-[1.7] text-body">
                <strong className="text-foreground">FCP</strong> (First Contentful Paint) — when anything first
                shows. <strong className="text-foreground">TTFB</strong> (Time to First Byte) — server response
                time.
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Try it in ten seconds</h2>
            <p className="mb-6 text-lg leading-[1.7] text-body">
              The fastest way to understand a browser check is to run one. Paste any URL and you'll get a
              screenshot plus its Core Web Vitals — free, no signup.
            </p>
            <BrowserCheckCta />
          </section>

          <section className="mb-10">
            <h2 className="font-sans mb-4 text-2xl font-semibold">From one-off check to monitoring</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              A single check is a snapshot. Monitoring is the same check on a schedule, with alerts when it fails
              or slows down. With Uptrack you can turn the page load into a scripted flow — log in, click through a
              funnel, submit a form — and run it every few minutes from a real browser.
            </p>
            <p className="text-lg leading-[1.7] text-body">
              Every run keeps its screenshot and Web Vitals, so you get a visual history and a performance trend,
              not just a green dot. When something breaks, the alert comes with a picture of exactly what your
              users saw.
            </p>
          </section>

          <div className="font-sans mt-12 rounded-lg border border-border bg-muted p-6 text-center">
            <h2 className="mb-2 text-2xl font-semibold">Monitor your critical pages</h2>
            <p className="mx-auto mb-6 max-w-lg text-base text-body">
              Pro includes 50 monitors at 30-second checks. Add a browser check for your most important flow.
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center rounded-lg bg-cta px-6 py-3 font-medium text-cta-foreground hover:opacity-90"
            >
              View plans <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <p className="mt-4 text-sm text-muted-foreground">
              Want to see it first?{' '}
              <Link to="/tools/browser-check" className="font-medium text-primary hover:underline">
                Run a free browser check
              </Link>{' '}
              on any URL — screenshot + Core Web Vitals, no signup.
            </p>
          </div>
        </article>
      </>
      </>
  )
}
